import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import React, { useEffect, useRef, Suspense, lazy } from "react";
import { CursorProvider, useCursor } from "@/context/CursorContext";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";

// Import HomePage normally since it shouldn't use Suspense
import HomePage from "@/pages/HomePage";

// Lazy load all other pages
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const CaseStudyDetailPage = lazy(() => import("@/pages/CaseStudyDetailPage"));
const BlocksPage = lazy(() => import("@/pages/BlocksPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Importy komponentów dla dokumentacji
import ComponentsPage from "@/pages/ComponentsPage";

import Footer from "@/components/Footer";
import CustomCursor from "@/components/CustomCursor";
import Stairs from "@/components/Stairs";
import Navbar from "@/components/Navbar";
import PageLoader from "@/components/PageLoader";

// Custom cursor reset component that uses location changes to reset cursor
const CursorReset = () => {
  const location = useLocation();
  const { setCursorVariant, setCursorText } = useCursor();

  useEffect(() => {
    // Reset cursor state on route change
    setCursorVariant("default");
    setCursorText("");
  }, [location.pathname]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <Stairs>
              <HomePage />
            </Stairs>
          }
        />
        <Route
          path="/contact"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <ContactPage />
              </Suspense>
            </Stairs>
          }
        />
        <Route
          path="/case-studies"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <CaseStudiesPage />
              </Suspense>
            </Stairs>
          }
        />
        <Route
          path="/case-study/:slug"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <CaseStudyDetailPage />
              </Suspense>
            </Stairs>
          }
        />
        <Route
          path="/blog"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <BlogPage />
              </Suspense>
            </Stairs>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <BlogPostPage />
              </Suspense>
            </Stairs>
          }
        />
        {/* Nowe trasy dla zoptymalizowanej wersji strony komponentów */}
        <Route
          path="/components"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <ComponentsPage />
              </Suspense>
            </Stairs>
          }
        />
        <Route
          path="/blocks"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <BlocksPage />
              </Suspense>
            </Stairs>
          }
        />
        <Route
          path="*"
          element={
            <Stairs>
              <Suspense fallback={<PageLoader />}>
                <NotFoundPage />
              </Suspense>
            </Stairs>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

// Main content component that includes the cursor context and reset
const AppContent = () => {
  const cursorRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <CursorProvider>
      <div ref={cursorRef} className="min-h-screen">
        <CustomCursor cursorRef={cursorRef} />
        <CursorReset />
        <Navbar />
        <AnimatedRoutes />
        <Footer />
      </div>
    </CursorProvider>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default () => (
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
